import { DOCUMENT } from '@angular/common';
import { Component, Input, TemplateRef, ViewChild, inject } from '@angular/core';
import { NgbOffcanvas, NgbOffcanvasOptions, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { OffcanvasConfig } from './offcanvas.config';

@Component({
    selector: 'app-offcanvas',
    templateUrl: './offcanvas.component.html',
    standalone: false
})
export class OffcanvasComponent {
  @Input() public offcanvasConfig: OffcanvasConfig;
  @Input() public showDivider = false;

  @ViewChild('offcanvas') private offcanvas: TemplateRef<OffcanvasComponent>;

  private readonly document = inject(DOCUMENT);
  private currentOffcanvasRef: NgbOffcanvasRef | null = null;

  constructor(
    private offcanvasService: NgbOffcanvas
  ) {
  }

  open(options: NgbOffcanvasOptions | undefined = undefined) {
    this.openWithOptions(options);
  }

  openLeft() {
    this.openWithOptions({ position: 'start' });
  }

  openRight(panelClass?: string) {
    this.openWithOptions({ position: 'end', panelClass });
  }

  openTop() {
    this.openWithOptions({ position: 'top' });
  }

  openBottom() {
    this.openWithOptions({ position: 'bottom' });
  }

  openNoBackdrop() {
    this.openWithOptions({ backdrop: false });
  }

  openStaticBackdrop() {
    this.openWithOptions({ backdrop: 'static' });
  }

  openScrollableContent() {
    this.openWithOptions({ scroll: true });
  }

  openNoKeyboard() {
    this.openWithOptions({ keyboard: false });
  }

  //Method for offcanvas that requires no keyboard interaction, such as forms where accidental close via 'Esc' button.
  openRightNoKeyboard(panelClass?: string) {
    this.openWithOptions({ position: 'end', panelClass, keyboard: false, backdrop: 'static' });
  }

  async dismiss() {
    if (this.offcanvasConfig.disableDismissButton !== undefined && this.offcanvasConfig.disableDismissButton())
      return;


    if (this.offcanvasConfig.shouldDismiss === undefined || (await this.offcanvasConfig.shouldDismiss())) {
      const result = this.offcanvasConfig.onDismiss === undefined || (await this.offcanvasConfig.onDismiss());

      if (this.currentOffcanvasRef) {
        this.currentOffcanvasRef.dismiss(result);
        return;
      }

      this.offcanvasService.dismiss(result);
    }
  }

  private openWithOptions(options: NgbOffcanvasOptions | undefined): void {
    const offcanvasRef = this.offcanvasService.open(this.offcanvas, options);
    this.currentOffcanvasRef = offcanvasRef;

    offcanvasRef.hidden.subscribe(() => {
      if (this.currentOffcanvasRef === offcanvasRef) {
        this.currentOffcanvasRef = null;
      }

      //added set timeout to defer the cleanup after offcanvas is fully hidden
      setTimeout(() => {
        this.cleanupBodyScrollLock();
      })
    });
  }

  private cleanupBodyScrollLock(): void {
    const hasVisibleOffcanvas = !!this.document.querySelector('.offcanvas.show');
    const hasVisibleBackdrop = !!this.document.querySelector('.offcanvas-backdrop.show');

    if (hasVisibleOffcanvas || hasVisibleBackdrop) {
      return;
    }

    this.document.body.style.removeProperty('overflow');
    this.document.body.style.removeProperty('padding-right');
    this.document.body.classList.remove('offcanvas-open');
  }
}
